'use client';

import React from 'react';
import { T, ORANGE, MONO, BODY, ACOL, AGENTS, lvlOf } from '@/lib/cleanTokens';

// ============================================================
// CSS KEYFRAMES - injected once via <style> tag
// ============================================================
export const CLEAN_CSS = `
  @import url('https://fonts.googleapis.com/css2?family=IBM+Plex+Mono:wght@400;500&family=Poppins:wght@400;500;600&display=swap');

  @keyframes cl-in-k {
    from { opacity: 0; transform: translateY(8px); }
    to   { opacity: 1; transform: translateY(0); }
  }
  .cl-in { animation: cl-in-k 0.35s cubic-bezier(.22,.9,.24,1); }

  @keyframes cl-toast-k {
    from { opacity: 0; transform: translateY(6px); }
    to   { opacity: 1; transform: translateY(0); }
  }
  .cl-toast { animation: cl-toast-k 0.25s ease-out; }

  @keyframes cl-pulse-k {
    0%,100% { opacity: 1; }
    50%      { opacity: 0.45; }
  }
  .cl-pulse { animation: cl-pulse-k 1.8s ease-in-out infinite; }

  @keyframes cl-pulse-soft-k {
    0%,100% { opacity: 1; }
    50%      { opacity: 0.75; }
  }
  .cl-pulse-soft { animation: cl-pulse-soft-k 2.4s ease-in-out infinite; }

  @keyframes cl-stripes-k {
    to { background-position: 24px 0; }
  }
  .cl-stripes {
    background-image: repeating-linear-gradient(
      45deg,
      rgba(255,255,255,0.45) 0 6px,
      transparent 6px 12px
    );
    background-size: 24px 100%;
    animation: cl-stripes-k 0.7s linear infinite;
  }

  @keyframes cl-head-k {
    0%,100% { transform: scale(0.75); opacity: 0.85; }
    50%      { transform: scale(1.25); opacity: 1; }
  }
  .cl-head { animation: cl-head-k 1.1s ease-in-out infinite; }

  @keyframes cl-breathe-k {
    0%,100% { filter: brightness(1); }
    50%      { filter: brightness(1.18); }
  }
  .cl-breathe { animation: cl-breathe-k 2.6s ease-in-out infinite; }

  .cl-eq {
    display: inline-flex;
    align-items: flex-end;
    gap: 1.5px;
    height: 9px;
  }
  .cl-eq i {
    width: 2.5px;
    background: currentColor;
    border-radius: 2px;
    animation: cl-eq-k 1s ease-in-out infinite;
  }
  .cl-eq i:nth-child(1) { height: 60%; animation-delay: 0s; }
  .cl-eq i:nth-child(2) { height: 100%; animation-delay: 0.18s; }
  .cl-eq i:nth-child(3) { height: 75%; animation-delay: 0.36s; }

  @keyframes cl-eq-k {
    0%,100% { transform: scaleY(0.45); }
    50%      { transform: scaleY(1); }
  }

  .cl-btn { transition: transform 0.12s ease, background 0.15s; }
  .cl-btn:hover { transform: translateY(-1px); }
  .cl-btn:active { transform: translateY(0) scale(0.98); }

  @media (prefers-reduced-motion: reduce) {
    * { animation: none !important; }
  }
`;

// ============================================================
// BAR
// ============================================================
interface BarProps {
  pct: number;
  col?: string;
  h?: number;
  live?: boolean;
}

export function Bar({ pct, col = ORANGE, h = 5, live = false }: BarProps) {
  const height = live ? h + 2 : h;
  return (
    <div style={{ position: 'relative', height, background: T.soft, borderRadius: 99 }}>
      <div
        className={live ? 'cl-stripes' : ''}
        style={{
          width: `${pct}%`,
          height: '100%',
          background: col,
          borderRadius: 99,
          transition: 'width 0.4s',
          boxShadow: live ? `0 0 8px ${col}66` : 'none',
        }}
      />
      {live && (
        <span
          className="cl-head"
          style={{
            position: 'absolute',
            top: '50%',
            left: `${pct}%`,
            width: h + 5,
            height: h + 5,
            marginLeft: -(h + 5) / 2,
            marginTop: -(h + 5) / 2,
            borderRadius: 99,
            background: col,
            boxShadow: `0 0 10px ${col}`,
          }}
        />
      )}
    </div>
  );
}

// ============================================================
// LVL
// ============================================================
interface LvlProps {
  xp: number;
  col?: string;
  w?: number;
}

export function Lvl({ xp, col = ORANGE, w = 120 }: LvlProps) {
  const { l, pct } = lvlOf(xp);
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
      <span style={{ fontFamily: MONO, fontSize: 10, fontWeight: 500, color: T.ink, minWidth: 38 }}>
        LVL {l}
      </span>
      <div style={{ width: w }}>
        <Bar pct={pct} col={col} />
      </div>
    </div>
  );
}

// ============================================================
// DOT
// ============================================================
interface DotProps {
  col: string;
  pulse?: boolean;
}

export function Dot({ col, pulse = false }: DotProps) {
  return (
    <span
      className={pulse ? 'cl-pulse' : ''}
      style={{
        width: 8,
        height: 8,
        borderRadius: 99,
        background: col,
        display: 'inline-block',
        flexShrink: 0,
      }}
    />
  );
}

// ============================================================
// EQ (equaliser bars)
// ============================================================
interface EqProps {
  col?: string;
}

export function Eq({ col = '#1FA65A' }: EqProps) {
  return (
    <span className="cl-eq" style={{ color: col }}>
      <i /><i /><i />
    </span>
  );
}

// ============================================================
// PILL
// ============================================================
interface PillProps {
  col: string;
  children: React.ReactNode;
}

export function Pill({ col, children }: PillProps) {
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 5,
        fontFamily: MONO,
        fontSize: 9,
        letterSpacing: 0.6,
        color: col,
        background: `${col}12`,
        border: `1px solid ${col}30`,
        padding: '3px 9px',
        borderRadius: 99,
        whiteSpace: 'nowrap',
      }}
    >
      {children}
    </span>
  );
}

// ============================================================
// MONOGRAM
// ============================================================
interface MonogramProps {
  id: number;
  size?: number;
}

export function Monogram({ id, size = 26 }: MonogramProps) {
  const a = AGENTS[id] ?? AGENTS[0];
  return (
    <span
      style={{
        width: size,
        height: size,
        borderRadius: 9,
        background: `${ACOL[id]}16`,
        color: ACOL[id],
        border: `1px solid ${ACOL[id]}33`,
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontFamily: MONO,
        fontWeight: 500,
        fontSize: size * 0.42,
        flexShrink: 0,
      }}
    >
      {a.code}
    </span>
  );
}

// ============================================================
// H - heading helper
// ============================================================
interface HProps {
  children: React.ReactNode;
  size?: number;
}

export function H({ children, size = 15 }: HProps) {
  return (
    <div
      style={{
        fontFamily: BODY,
        fontWeight: 600,
        fontSize: size,
        color: T.ink,
        letterSpacing: 0.1,
      }}
    >
      {children}
    </div>
  );
}
